import React, { useState, useEffect } from 'react';
import { sendContactEmail, fetchFormToken } from '../utils/sendEmail';
import './CTASection.css';

const SERVICES = [
  'Software Development',
  'Cybersecurity Services',
  'Blockchain & Crypto',
  'Electronic Intrusion Detection',
  'Education Technology',
  'Biotech & Longevity',
  'Immigration & Citizenship',
  'Corporate Services',
  'Business Advisory',
  'Emirati & Contemporary Art'
];

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  contact: '',
  service: '',
  message: ''
};

const CTASection = () => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [formError, setFormError] = useState('');

  // Same anti-spam pair as the contact page: hidden honeypot + signed token.
  const [honeypot, setHoneypot] = useState('');
  const [formToken, setFormToken] = useState(null);

  useEffect(() => {
    let active = true;
    fetchFormToken().then((token) => {
      if (active) setFormToken(token);
    });
    return () => { active = false; };
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!formData.email) {
      setFormError('Please enter your email address.');
      return;
    }
    if (formData.message.trim().length < 15) {
      setFormError('Please tell us a little about what you need (at least 15 characters).');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const result = await sendContactEmail(
        { ...formData, source: 'Homepage consultation form' },
        formToken,
        honeypot
      );

      if (result.success) {
        setSubmitStatus('success');
        setFormData(EMPTY_FORM);
        fetchFormToken().then(setFormToken);
      } else {
        setSubmitStatus('error');
        setFormError(result.error || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      setSubmitStatus('error');
      setFormError('An error occurred. Please try again.');
      console.error('Consultation form error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="cta-section">
      <div className="cta-container">
        <div className="cta-split-layout">
          <div className="cta-left">
            <div className="cta-form-container">
              <h3 className="form-title">Embark on Your Global Journey</h3>
              <p className="form-subtitle">
                Connect with our experts to explore immigration, investment, and business opportunities across six continents.
              </p>
              <form className="cta-form" onSubmit={handleSubmit}>
                {/* Honeypot - hidden from people, irresistible to bots. */}
                <div className="form-hp" aria-hidden="true">
                  <label htmlFor="cta_company_url">Company URL (leave blank)</label>
                  <input
                    type="text"
                    id="cta_company_url"
                    name="company_url"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    tabIndex="-1"
                    autoComplete="off"
                  />
                </div>

                <div className="form-row">
                  <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} placeholder="First Name" className="cta-form-input" />
                  <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} placeholder="Last Name" className="cta-form-input" />
                </div>
                <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="Email Address" className="cta-form-input full-width" required />
                <input type="tel" name="contact" value={formData.contact} onChange={handleChange} placeholder="Phone Number" className="cta-form-input full-width" />
                <select name="service" value={formData.service} onChange={handleChange} className="cta-form-input full-width">
                  <option value="">Select Service Interest</option>
                  {SERVICES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us about your specific needs - citizenship goals, business formation requirements, investment interests, or other services..."
                  className="cta-form-textarea"
                  required
                ></textarea>
                <button type="submit" className="form-submit-btn" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Schedule Consultation'}
                  <span className="cta-btn-arrow">→</span>
                </button>

                {submitStatus === 'success' && (
                  <p className="form-success-notice">
                    Thank you. Our team will be in touch shortly.
                  </p>
                )}

                {formError && (
                  <p className="form-error-notice">{formError}</p>
                )}
              </form>
            </div>
          </div>
          
          <div className="cta-right">
            <div className="cta-content-right">
              <h2 className="cta-title-v3">Unlock Global Opportunities</h2>
              <p className="cta-description-v3">
                From software development and cybersecurity services to blockchain & crypto, electronic intrusion detection, education technology, biotech & longevity, immigration & citizenship, corporate services, business advisory, and Emirati & contemporary art, Rayad Group has delivered comprehensive solutions across 6 continents since 1994.
              </p>
              
              <div className="feature-list">
                <div className="feature-item">
                  <div className="feature-text">
                    <strong>Enterprise Security</strong>
                    <span>AI-Powered Threat Intelligence and Compliance</span>
                  </div>
                </div>
                <div className="feature-item">
                  <div className="feature-text">
                    <strong>Global Citizenship Programs</strong>
                    <span>EB-5 USA, St. Kitts & Nevis, and Vanuatu</span>
                  </div>
                </div>
                <div className="feature-item">
                  <div className="feature-text">
                    <strong>Business Formation</strong>
                    <span>UAE Free Zones, Legal, and Financial Services</span>
                  </div>
                </div>
              </div>
              
              <div className="trust-indicators">
                <div className="trust-item">
                  <span className="trust-number">6</span>
                  <span className="trust-label">Continents</span>
                </div>
                <div className="trust-item">
                  <span className="trust-number">150+</span>
                  <span className="trust-label">Country Access</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTASection;